import type { VideoItem } from './components/youtube-helpers';
import type { IGSetupGuideItem } from './types';
import type { CSSProperties } from 'react';

import { ONBOARDING_USER_SCENARIO } from '~/modules/onboarding/constants/onboarding-steps';
import { Bleed, BlockStack, Box, Button, Icon, InlineStack, Text } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GSetupGuideItem, GSetupGuideVideoTutorial } from './components';
import { useGSetupGuideContext } from './contexts/GSetupGuideProvider';
import { CompassIcon, XIcon } from '@shopify/polaris-icons';
import { useSetupGuideState } from './useSetupGuideState';
import { useOnboardingState } from '~/modules/onboarding';
import { cls, getPageHeaderHeight } from '~/utils';
import { ISetupGuideButtonStatus } from './types';
import { SETUP_GUIDE_CONFIG } from './constants';
import { useTranslation } from 'react-i18next';
import { GBoxCard } from '~/presentation/ui';
import { useWindowSize } from '../../hooks';

const ADVANCE_SETTING_PADDING_TOP = 4;

const ONBOARDING_VIDEOS: VideoItem[] = Object.values(ONBOARDING_USER_SCENARIO)
  .filter((s) => s.videoLink)
  .map((s) => ({
    title: s.content,
    videoLink: s.videoLink,
  }));

const getSetupGuideButtonStatus = (item: IGSetupGuideItem): ISetupGuideButtonStatus => {
  if (item.isHiddenAction) return ISetupGuideButtonStatus.HIDDEN;
  if (item.disabledReason) return ISetupGuideButtonStatus.DISABLED;

  return ISetupGuideButtonStatus.NORMAL;
};

interface GSetupGuideProps<T extends string> {
  setupGuideState: boolean;
  items: IGSetupGuideItem<T>[];
  campaignName: string;
  campaignId?: string;
  showVideoTutorial?: boolean;
  handleUpdateSetupGuide: (setupGuideKeys: T[]) => void;
}

export const GSetupGuide = <T extends string>({
  items,
  campaignName,
  campaignId,
  showVideoTutorial = true,
  handleUpdateSetupGuide,
}: GSetupGuideProps<T>) => {
  const isCollapsedAfterFirstCompletedSetup = useRef<boolean>(false);

  const { t } = useTranslation();
  const { windowWidth } = useWindowSize();
  const { setupGuideState, setHideSetupGuide } = useSetupGuideState();
  const { expandedId, setExpandedId, handleCloseHighLight } = useGSetupGuideContext();
  const {
    showOnboardingModal,
    onboardingData: {
      campaignId: onboardingCampaignId,
      campaignGroupId: onboardingCampaignGroupId,
      userScenario,
    },
  } = useOnboardingState();

  const initialVideoIndex = useMemo(() => {
    if (!campaignId || ![onboardingCampaignId, onboardingCampaignGroupId].includes(campaignId))
      return 0;

    const idx = ONBOARDING_VIDEOS.findIndex((v) => v.title === userScenario);
    return idx >= 0 ? idx : 0;
  }, [campaignId, onboardingCampaignGroupId, onboardingCampaignId, userScenario]);

  const [isHiddenOnFirstMount, setIsHiddenOnFirstMount] = useState<boolean>(false);
  const [setupGuidePosition, setSetupGuidePosition] = useState<{
    top: number;
    right: number;
  }>({
    top: 0,
    right: 0,
  });

  const handleCloseSetupGuide = useCallback(() => {
    handleCloseHighLight();
    setHideSetupGuide();
    setExpandedId(undefined);
  }, [handleCloseHighLight, setHideSetupGuide, setExpandedId]);

  useEffect(() => {
    if (items.some((item) => !item.completed) || isCollapsedAfterFirstCompletedSetup.current)
      return;

    isCollapsedAfterFirstCompletedSetup.current = true;
    setExpandedId(undefined);
  }, [items, setExpandedId]);

  useEffect(() => {
    setupGuideState && setIsHiddenOnFirstMount(true);
  }, [setupGuideState]);

  useEffect(() => {
    const pageHeaderHeight = getPageHeaderHeight();

    setSetupGuidePosition(
      !windowWidth.xsDown
        ? { top: pageHeaderHeight + ADVANCE_SETTING_PADDING_TOP, right: 24 }
        : { top: 0, right: 0 },
    );
  }, [windowWidth, campaignName, showOnboardingModal]);

  return (
    <div
      className={cls('GSetupGuide-container', {
        'GSetupGuide-container--visible': setupGuideState,
        'GSetupGuide-container--hidden': !setupGuideState && isHiddenOnFirstMount,
      })}
      style={
        {
          top: `${setupGuidePosition.top}px`,
          right: `${setupGuidePosition.right}px`,
          '--gp-setup-guide-animation-duration': `${SETUP_GUIDE_CONFIG.ANIMATION_TIME_IN_MS}ms`,
          ...(!setupGuideState ? { display: 'none' } : {}),
        } as CSSProperties
      }
    >
      <GBoxCard minHeight="100%">
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack align="center" gap="100">
              <Icon source={CompassIcon} tone="base" />

              <Text as="h3" variant="headingMd" fontWeight="semibold">
                {t('Setup guide')}
              </Text>
            </InlineStack>

            <Button variant="tertiary" icon={XIcon} onClick={handleCloseSetupGuide} />
          </InlineStack>

          {showVideoTutorial && (
            <GSetupGuideVideoTutorial
              videos={ONBOARDING_VIDEOS}
              initialIndex={initialVideoIndex}
              isGuideVisible={setupGuideState}
            />
          )}

          <Bleed marginInline="400">
            <Box
              paddingInline="400"
              paddingBlockStart="300"
              borderColor="border-secondary"
              borderBlockStartWidth="025"
            >
              <Text as="h4" variant="headingMd" tone="subdued">
                {t('Steps guide')}
              </Text>
            </Box>
          </Bleed>

          <Bleed marginInline="200">
            <BlockStack>
              {items.map((item, index) => (
                <GSetupGuideItem
                  key={item.id}
                  item={item}
                  buttonStatus={getSetupGuideButtonStatus(item)}
                  tooltipContent={item.disabledReason}
                  nextItemId={items[index + 1]?.id}
                  expanded={expandedId === item.id}
                  setExpandedId={setExpandedId}
                  handleMarkDone={() => {
                    if (!item.isMarkDoneByAction) handleUpdateSetupGuide([item.id]);
                  }}
                />
              ))}
            </BlockStack>
          </Bleed>
        </BlockStack>
      </GBoxCard>
    </div>
  );
};
